import { useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";

const DEMO_URL = "/api/demo-app";

export function DemoFrame() {
  const frameRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);

  const fit = () => {
    const frame = frameRef.current;
    if (!frame) return;
    try {
      const doc = frame.contentDocument;
      if (!doc || !doc.body) return;
      const footer = doc.querySelector("footer") || doc.querySelector("[class*=footer i]");
      const footerH = footer ? footer.getBoundingClientRect().height : 240;
      const total = Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight);
      if (total > 400) frame.style.height = `${Math.max(600, total - footerH - 12)}px`;
    } catch {
      /* keep fallback height if the inner document is not readable */
    }
  };

  useEffect(() => {
    const timers = [200, 700, 1500, 3000, 5000].map((t) => setTimeout(fit, t));
    window.addEventListener("resize", fit);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", fit);
    };
  }, []);

  const retry = () => {
    setFailed(false);
    setNonce((n) => n + 1);
  };

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-ink/10" data-testid="demo-frame-wrap">
      <div className="flex items-center gap-3 border-b border-slate-200 bg-sand/60 px-5 py-3">
        <span className="flex flex-shrink-0 gap-1.5">
          <span className="h-3 w-3 rounded-full bg-red-400" />
          <span className="h-3 w-3 rounded-full bg-amber-400" />
          <span className="h-3 w-3 rounded-full bg-emerald-400" />
        </span>
        <span className="mx-auto truncate rounded-full bg-white px-4 py-1 font-mono text-xs text-slate-500" data-testid="demo-url-pill">
          live platform demo · restricted functionalities
        </span>
      </div>
      {failed ? (
        <div className="flex h-[60vh] flex-col items-center justify-center gap-4 bg-sand/40 p-8 text-center">
          <p className="font-display text-lg font-bold text-ink">The live demo could not load in this frame.</p>
          <p className="max-w-md text-sm text-slate-600">Please wait a moment and try again — the demo runs right here on the page.</p>
          <button type="button" onClick={retry} className="btn-primary" data-testid="demo-retry-btn">
            <RotateCcw className="h-4 w-4" /> Retry
          </button>
        </div>
      ) : (
        <iframe
          ref={frameRef}
          src={nonce ? `${DEMO_URL}?_r=${nonce}` : DEMO_URL}
          title="Live platform demo"
          scrolling="no"
          onLoad={fit}
          onError={() => setFailed(true)}
          className="block w-full border-0"
          style={{ height: "70vh" }}
          data-testid="demo-frame"
        />
      )}
    </div>
  );
}

export default function Demo() {
  return (
    <>
      <PageHero
        eyebrow="Demo"
        lines={[<>The platform, <span className="font-serif italic font-medium text-brand">live in action</span></>]}
        desc={<>Explore a demo preview of the learning platform below. Some functionalities are restricted — everything else is right there for you to see.<br />Your admission counselor can login and demo the platform.</>}
        testid="demo-page-heading"
      />
      <section className="container-x pb-24" data-testid="demo-section">
        <DemoFrame />
      </section>
    </>
  );
}
